import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { CreateProductRequest, UpdateProductRequest, Product, StarlinkProduct } from '@/shared/types/productTypes';
import { PaginatedResponse, SingleResponse } from '@/shared/types/responseTypes';

/** Infinite scroll fetch for products load-more. */
export const useInfiniteProducts = (regionCode: string | undefined) => {
  return useInfiniteQuery({
    queryKey: ['region-products-infinite', regionCode],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<Product>>(
        `/enterprise/v1/products`,
        { params: { regionCode, page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
    enabled: !!regionCode,
  });
};

// Create Product Mutation
export const useCreateProduct = (regionCode: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProductRequest) => {
      return apiClient.post<SingleResponse<Product>>(
        `/enterprise/v1/products`,
        {
          params: { regionCode },
          body: data,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['created-product'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Add Product to Organization Mutation
export const useAddProductToOrganization = (productId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: string) => {
      return apiClient.post<APIMessageResponse>(
        `/enterprise/v1/products/${productId}/organizations/${organizationId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['added-product-to-account'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove Product from Organization Mutation
export const useRemoveProductFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, organizationId }: { productId: string; organizationId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/products/${productId}/organizations/${organizationId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['removed-product-from-account'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Update Product Mutation
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, data }: { productId: string, data: UpdateProductRequest }) => {
      return apiClient.put<APIMessageResponse>(
        `/enterprise/v1/products/${productId}`,
        {
          body: data,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['updated-product'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Delete Product Mutation
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId }: { productId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/products/${productId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deleted-product'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Use Starlink Products Query
export const useStarlinkProducts = (enabled: boolean, regionCode: string | undefined) => {
  return useQuery({
    queryKey: ['starlink-products', regionCode],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<StarlinkProduct>>(
        '/enterprise/v1/products/starlink-products',
        { params: { regionCode } }
      );
      return response.data.results;
    },
    enabled: enabled && !!regionCode
  });
};